import {
  type Logger,
  getLogger,
  postAppBridgeMessage,
  registerNotificationHandler,
  unregisterNotificationHandler,
  clearJsCallTimer,
  getJsCallId,
} from '../../bridge';
import {
  Bridge,
  NativeAPIResponseMessage,
  BridgeOptions,
} from '@zoom/hybrid-jsbridge';
import { genRandomId } from '../utils';
import { isPending } from '../../utils/promise';
import apps from '../apps';

type TimerInfo = {
  service: string;
  action: string;
  jsCallId: string;
  timeout: number;
  beginTime: number;
};
let appBridgeInited = false;
let appBridgeReady = false;
const registeredEvents: Set<string> = new Set();
const ignoreEvents: Set<string> = new Set();
const pendingRequest: Map<
  RequestId,
  { resolve: (p: any) => void; reject: (e: Error) => void }
> = new Map();
let appEventHandler: (e: EventNotify) => void;
const pendingTimer: Map<RequestId, any> = new Map();
const pendingTimerInfo: Map<RequestId, TimerInfo> = new Map();
let logger: Logger;

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.config
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
async function config(cfg: Config) {
  // Skip if appBridge is already initialized
  if (appBridgeInited) {
    logger.warn(
      'hybrid.appBridge skip config, appBridge already configured, please call reset() then re-config!'
    );
    return {
      returnCode: 0,
      result: 'Success',
      message:
        'hybrid.appBridge skip config, appBridge already configured, please call reset() then re-config!',
    };
  }

  appBridgeInited = true;

  // Initialize bridge instance if config exists
  if (cfg.js_bridge_config) {
    Bridge.getInstance(cfg.js_bridge_config);
  }

  // Configure logging if enabled
  if (cfg.log) {
    configLog(cfg);
  }

  // Create a timeout promise that resolves after 100ms
  const timeoutPromise = new Promise<void>((resolve) => {
    setTimeout(resolve, 100);
  });

  // if config not supported, we need to manual remove timer of config API.
  const configJsCallId = genRandomId();
  const js_api_lists = Array.from(
    new Set([
      'zmAppBridgePub',
      'zmAppBridgeSub',
      'zmOnAppBridgeEvent',
      ...(cfg.js_api_lists || []),
    ])
  );
  logger = getLogger();

  if (cfg.auto_config_apps) {
    // don't add await, some webview may not respond to config API
    apps
      .config({ capabilities: js_api_lists })
      .then((res) => {
        logger.info(
          'hybrid.appBridge auto_config_apps is enabled, apps.config response with',
          res
        );
      })
      .catch((e) => {
        logger.warn(
          'hybrid.appBridge auto_config_apps is enabled, but the app config does not respond!',
          e
        );
      });
    logger.info(
      'hybrid.appBridge auto_config_apps is enabled, call apps config finished! waiting response...'
    );
  }

  // Start configJsAPIList with timeout handling in parallel
  // If it doesn't complete within 100ms, the promise will resolve silently
  const configRequest = configJsAPIList({ js_api_lists }, configJsCallId);
  const jsAPIListPromise = Promise.race([configRequest, timeoutPromise])
    .then(async () => {
      if (await isPending(configRequest)) {
        clearJsCallTimer(configJsCallId);
        logger.warn(
          'hybrid.appBridge skip config API, remove config API timer after 100ms'
        );
      }
    })
    .catch((e) => {
      logger.error('hybrid.appBridge configJsAPIList error', e);
    });

  // Configure event handling
  eventConfig(cfg);
  registerNotificationHandler(appBridgeEventHandler);

  try {
    if (cfg.subscribe?.length) {
      // Execute configSubscribe first to not block it
      const subscribeRes = await configSubscribe(cfg)!;

      // Wait for jsAPIList to complete or timeout
      // This won't block the response since we already have subscribeRes
      await jsAPIListPromise;

      if (subscribeRes.returnCode === 0) {
        logger.info('hybrid.appBridge config successfully! 🚀');
        appBridgeReady = true;
        return subscribeRes;
      } else {
        throw new Error(
          `hybrid.appBridge configSubscribe failed, please check your subscribe config, returnCode: ${subscribeRes.returnCode}`
        );
      }
    } else {
      appBridgeReady = true;
      return {
        returnCode: 0,
        result: 'Success',
        message:
          'hybrid.appBridge config app bridge finished. subscribe is empty, skipping call to zmAppBridgeSub.',
      };
    }
  } catch (e) {
    // Reset state on error
    reset();
    throw e;
  }
}

function configSubscribe(subscribeConfig: AppBridgeSubscribeConfig) {
  if (subscribeConfig.subscribe && subscribeConfig.subscribe.length > 0) {
    subscribeConfig.subscribe.forEach(({ eventNames }) => {
      eventNames?.forEach((eventName) => {
        registeredEvents.add(eventName);
      });
    });

    return postAppBridgeMessage<
      { data: AppBridgeSubscribe[] },
      NativeAPIResponseMessage<string>
    >('zmAppBridgeSub', { data: subscribeConfig.subscribe });
  }
}

function configJsAPIList(apiConfig: AppBridgeAPIConfig, jsCallId: string) {
  return postAppBridgeMessage<
    AppBridgeAPIConfig,
    NativeAPIResponseMessage<AppBridgeConfigResponse>
  >('config', apiConfig, { jsCallId });
}

function eventConfig(cfg: IgnoreEventsConfig & OnAppEventNotifyConfig) {
  cfg.ignoreEvents?.forEach((ignoreEvent) => {
    ignoreEvents.add(ignoreEvent);
  });

  if (cfg.onAppEventNotified) {
    appEventHandler = cfg.onAppEventNotified;
  }
}

function configLog(logConfig: LogConfig) {
  Bridge.getInstance({
    debug: logConfig.log,
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.reset
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
function reset() {
  if (!appBridgeInited) return;
  appBridgeInited = false;
  appBridgeReady = false;
  unregisterNotificationHandler(appBridgeEventHandler);
  registeredEvents.clear();
  ignoreEvents.clear();
  pendingRequest.clear();
  appEventHandler = () => {};
  pendingTimer.clear();
  pendingTimerInfo.clear();
}

export type Service = {
  pub: <T, R>(pubMessage: PubMessage<T>) => Promise<R>;
};

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.getService
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
function getService(config: { service: string }): Service | undefined {
  const { service } = config;
  if (!appBridgeInited) {
    throw new Error(
      '[Hybrid.appBridge] App bridge is not configured, please call config() first!'
    );
  }
  if (!appBridgeReady) {
    throw new Error(
      '[Hybrid.appBridge] App bridge is not ready, please await config() finish!'
    );
  }
  if (!service) {
    throw new Error(
      `[Hybrid.appBridge] App bridge getService got a empty service [${service}]!`
    );
  }
  const serviceCache: {
    [key: string]: Service;
  } = {};
  if (!serviceCache[service]) {
    serviceCache[service] = {
      pub: <T, R>(pubMessage: PubMessage<T>) => {
        const {
          action,
          params = {} as T,
          jsCallId: overrideJsCallId,
          timeout = 30 * 1000,
          needResponse = true,
        } = pubMessage;
        const jsCallId = overrideJsCallId || getJsCallId();
        const data = JSON.stringify({
          action,
          params,
          jsCallId,
        });
        const promise: Promise<R> = new Promise((resolve, reject) => {
          pendingRequest.set(jsCallId, { resolve, reject });
        });
        if (needResponse) {
          addTimerForRequest({ service, action, jsCallId, timeout });
        } else {
          resolvedDirectly(jsCallId);
        }
        postAppBridgeMessage(
          'zmAppBridgePub',
          { data, service },
          overrideJsCallId ? { jsCallId } : {}
        )
          .then((res) => {
            if (res && res.returnCode !== 0) {
              rejectRequest(jsCallId, res);
            }
          })
          .catch((e) => {
            pendingRequest.get(jsCallId)?.reject(e);
          });
        return promise;
      },
    };
  }

  return serviceCache[service];
}

function addTimerForRequest(request: {
  service: string;
  action: string;
  jsCallId: string;
  timeout: number;
}) {
  const { service, action, jsCallId, timeout } = request;
  if (timeout) {
    const timer = setTimeout(() => {
      logger.warn(
        `hybrid.appBridge Timeout: The native client did not provide a response to the App Bridge call, service:${service} action:${action}`
      );
      if (!pendingRequest.get(jsCallId)) {
        logger.error(`hybrid.appBridge missing timer for ${jsCallId}`);
      }
      pendingRequest.get(jsCallId)?.reject(
        new Error(
          `[hybrid/jsbridge]AppBridge timeout, service:${service} action:${action} ${JSON.stringify(
            {
              returnCode: RESULT_CODE.CALL_SDK_TIME_OUT,
              errorCode: RESULT_CODE.CALL_SDK_TIME_OUT,
            }
          )}`
        )
      );
      pendingRequest.delete(jsCallId);
      removeTimerById(jsCallId);
    }, timeout);
    pendingTimer.set(jsCallId, timer);
    pendingTimerInfo.set(jsCallId, {
      service,
      action,
      jsCallId,
      timeout,
      beginTime: Date.now(),
    });
  }
}

function resolvedDirectly(jsCallId: string) {
  pendingRequest.get(jsCallId)?.resolve({
    returnCode: RESULT_CODE.SUCCESS,
    errorCode: RESULT_CODE.SUCCESS,
  });
  pendingRequest.delete(jsCallId);
}

const appBridgeEventHandler = (eventData: NativeAPIResponseMessage<any>) => {
  const { returnCode, result: eventResult } = eventData;
  if (returnCode === 0) {
    const { eventName } = eventResult;
    let eventData = eventResult.eventData;
    if (registeredEvents.has(eventName)) {
      if (typeof eventData === 'string') {
        eventData = safeJsonParse(eventData);
      }
      if (eventData) {
        const { jsCallId, result } = eventData;

        if (jsCallId && !ignoreEvents.has(eventName)) {
          /**
           * 1. If there is no reqId, it means this is the final response data.
           * 2. If the response data contains reqId, it means this is an asynchronous notification request,
           * and a true response notification with the same reqId will follow shortly.
           */
          const { reqId } = result || {};
          if (reqId) {
            // wait async response from notification with reqId
            jsCallIdUpgradeToReqId(jsCallId, reqId);
          } else {
            // directly response
            resolvedRequest(jsCallId, eventData);
          }
        } else {
          // notification with reqId was received, resolve the request with reqId
          const reqId = getReqId(eventData);
          if (reqId) {
            resolvedRequest(reqId, eventData);
          }
        }
      }
      appEventHandler?.({ eventName, eventData });
    }
  } else {
    const jsCallId = eventResult?.eventData?.jsCallId;
    const eventName = eventResult?.eventName;
    const eventData = eventResult?.eventData;
    rejectRequest(jsCallId, eventData);
    appEventHandler?.({ eventName, eventData });
  }
};

function resolvedRequest(id: string, data: NativeAPIResponseMessage<any>) {
  pendingRequest.get(id)?.resolve(data);
  pendingRequest.delete(id);
  removeTimerById(id);
}

function rejectRequest(id: string, response: NativeAPIResponseMessage<any>) {
  if (!id) {
    logger.warn(
      `hybrid.appBridge can't rejectRequest with a empty jsCallId`,
      response
    );
  } else {
    pendingRequest
      .get(id)
      ?.reject(
        new ZoomAppBridgeError(
          `[hybrid/jssdk]AppBridge ErrorResponse ${JSON.stringify(response)}`,
          response
        )
      );
    pendingRequest.delete(id);
    removeTimerById(id);
  }
}

function jsCallIdUpgradeToReqId(jsCallId: string, reqId: string) {
  if (pendingRequest.has(jsCallId)) {
    const timerInfo = pendingTimerInfo.get(jsCallId);
    pendingRequest.set(reqId, pendingRequest.get(jsCallId)!);
    clearTimeout(pendingTimer.get(jsCallId));
    pendingTimer.delete(jsCallId);
    pendingTimerInfo.delete(jsCallId);
    pendingRequest.delete(jsCallId);
    if (timerInfo) {
      timerInfo.jsCallId = reqId;
      timerInfo.timeout = Math.max(
        1,
        timerInfo.timeout - (Date.now() - timerInfo.beginTime)
      );
      addTimerForRequest({ ...timerInfo });
    } else {
      logger.error(`hybrid.appBridge jsCallIdUpgradeToReqId failed`, timerInfo);
    }
  }
}

function getReqId(eventData: { reqId: string; req_id: string }) {
  const { reqId, req_id } = eventData;
  return reqId || req_id;
}

function safeJsonParse(json: string) {
  try {
    return JSON.parse(json);
  } catch (error) {
    logger.warn(
      `[hybrid/appBridge] safeJsonParse Parse error, invalid json ${json} ${error}`
    );
  }
  return null;
}

function removeTimerById(id: string) {
  clearTimeout(pendingTimer.get(id));
  pendingTimer.delete(id);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * appBridge.pub
 *
 * [PLATFORM]
 * mac        5.11.0
 * windows    5.11.0
 * linux      5.11.0
 * ios        5.11.0
 * android    5.11.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
// to support hybrid.isAvailable('appBridge.pub')

export enum RESULT_CODE {
  SUCCESS = 0,
  CALL_SDK_TIME_OUT = -100,
  CALL_ASYNC_NETWORK_ERROR = -101,
}

type PubMessage<T> = {
  action: string;
  params?: T;
  jsCallId?: string;
  timeout?: number; // seconds
  needResponse?: boolean;
};

type RequestId = string;
type Config = AppBridgeSubscribeConfig &
  IgnoreEventsConfig &
  OnAppEventNotifyConfig &
  AppBridgeAPIConfig &
  LogConfig &
  JSBridgeConfig;

type JSBridgeConfig = {
  js_bridge_config?: Partial<BridgeOptions>;
};

type AppBridgeAPIConfig = {
  js_api_lists?: string[];
  auto_config_apps?: boolean;
};
export type AppBridgeSubscribeConfig = {
  subscribe?: AppBridgeSubscribe[];
};
export type AppBridgeSubscribe = {
  service: string;
  eventNames: string[];
};

export type IgnoreEventsConfig = { ignoreEvents?: string[] };
export type LogConfig = {
  log?: {
    enable: boolean;
    output: {
      info: (...data: any[]) => void;
      warn: (...data: any[]) => void;
      error: (...data: any[]) => void;
    };
  };
};

export type OnAppEventNotifyConfig = { onAppEventNotified: EventNotifyHandler };
export type EventNotify = { eventName: string; eventData?: any };
export type EventNotifyHandler = (e: EventNotify) => void;

export type AppBridgeConfigResponse = {
  auth: {
    status: string;
    upgradeable: boolean;
  };
  browserVersion: string;
  clientVersion: string;
  launchContext: {
    openBy: string;
  };
  runningContext: string;
  unsupportedApis: string[];
  userAgent: string;
};

export type Pub<T> = {
  service: string;
  data: T;
};

export class ZoomAppBridgeError extends Error {
  public response: NativeAPIResponseMessage<any>;
  /** @hidden */
  constructor(message: string, response: NativeAPIResponseMessage<any>) {
    super(message);
    this.response = response;
    Object.setPrototypeOf(this, ZoomAppBridgeError.prototype);
  }
}

export default {
  config,
  reset,
  getService,
};
